import React, {
  ComponentType,
  useState
} from 'react'
import {
  Button,
  Form,
  Header,
  Message
} from 'semantic-ui-react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
// Local
import { LoginLogo } from '../common/brandingUtils'
import { history } from '../../store'
import {
  PAGES,
  shouldRenderReskin2025
} from '../../constant'
import If from '../common/if'
import ClinicalIntelligenceLogo from '../logos/ClinicalIntelligenceLogo'
import ReveleerLogoStacked from '../logos/ReveleerLogoStacked'
import ReveleerButton from '../common/ReveleerButton'
import ReveleerCoachingBox from '../ReveleerCoachingBox'


const SafeForm = Form as unknown as ComponentType<any>
const SafeHeader = Header as unknown as ComponentType<any>

/**
 * Provides a login form
 * @param id An ID attribute prefix used to identify component elements
 * @param onSubmit Login form submit callback function
 *        Arguments: username and password
 * @param username Optional default username
 * @param errorMessage Optional default error message
 */
type Props = {
  errorMessage: string | undefined
  id: string
  onSubmit: (
    username: string,
    password: string,
    token: string | null
  ) => void
  username: string | undefined
}

function LoginForm(
  props: Props
): JSX.Element {
  const {
    errorMessage: errorMessageProp,
    id: idProp,
    onSubmit: onSubmitProp,
    username: usernameProp
  } = props

  const { executeRecaptcha } = useGoogleReCaptcha()

  const [
    usernameState,
    setUsernameState
  ] = useState<string>(
    usernameProp || ''
  )
  const [
    passwordState,
    setPasswordState
  ] = useState<string>('')
  const [
    isSubmittingState,
    setIsSubmittingState
  ] = useState<boolean>(false)

  const onSubmit = async () => {
    if (!usernameState || !passwordState || isSubmittingState) return
    setIsSubmittingState(true)

    if (executeRecaptcha) {
      const token = await executeRecaptcha("login")
      if (token) {
        onSubmitProp(usernameState, passwordState, token)
      }
      
      setIsSubmittingState(false)
    }
  }

  const onChange = (
    e: any,
    value: {
      name: null
      value: null
    } | null
  ) => {
    if (
      value !== null &&
      value.name !== null &&
      value.value !== null
    ) {
      if (value.name === 'username') {
        setUsernameState(value.value)
        return
      }
      if (value.name === 'password') {
        setPasswordState(value.value)
      }
    }
  }

  const onForgotPassword = () => {
    history.push(PAGES.FORGOT_PASSWORD)
  }

  return (
    <SafeForm error={ errorMessageProp != null }>
      <SafeHeader
        as="h2"
        textAlign="center"
      >
        <Header.Content>
          <If isTrue={ !shouldRenderReskin2025 }>
            <LoginLogo />
          </If>
          <If isTrue={ shouldRenderReskin2025 }>
            <ReveleerLogoStacked />
            <ClinicalIntelligenceLogo />
          </If>
        </Header.Content>
      </SafeHeader>
      <If isTrue={ !!errorMessageProp }>
        <If isTrue={ !shouldRenderReskin2025 }>
          <Message
            error={ true }
            content={ errorMessageProp }
          />
        </If>
        <If isTrue={ shouldRenderReskin2025 }>
          <ReveleerCoachingBox
            coachingBoxType="error"
            isShowing
            message={
              <p>
                { errorMessageProp }
              </p>
            }
          />
        </If>
      </If>
      <Form.Input
        id={ `${idProp}-login-username` }
        label={
          shouldRenderReskin2025
            ? ''
            : 'Username:'
        }
        placeholder="Username"
        onChange={ onChange }
        name="username"
        value={ usernameState }
      />
      <Form.Input
        id={ `${idProp}-login-password` }
        label={
          shouldRenderReskin2025
            ? ''
            : 'Password:'
        }
        placeholder="Password"
        onChange={ onChange }
        name="password"
        value={ passwordState }
        type="password"
      />
      <div
        className="right-text linkable"
        id="forgotpassword"
        onClick={ onForgotPassword }>
        Forgot Password?
      </div>
      <If isTrue={ !shouldRenderReskin2025 }>
        <Button
          onClick={ onSubmit }
          disabled={ !usernameState.length || !passwordState.length }
          id={ `${idProp}-login-submit` }
          color="blue"
          type="submit"
        >
          Submit
        </Button>
      </If>
      <If isTrue={ shouldRenderReskin2025 }>
        <ReveleerButton
          buttonLabel="Submit"
          buttonSize="large"
          buttonType="primary"
          id=""
          isDisabled={ !usernameState.length || !passwordState.length }
          onClick={ onSubmit }
          type="submit"
        />
      </If>
    </SafeForm>
  )
}

export default LoginForm
